
import io, { Socket } from 'socket.io-client';
import { UserCache } from '../caches';
import { getAppSettings } from '../../hooks/settings/settings';
import { ApiService } from '../api-service';
import { JwtAuthService, setTokenRefresh, shouldRefreshToken } from '../authentication';

interface SocketItem {
    socket: Socket | null;
    name: string;
    pathName?: string;
}

interface ConnectionInfo {
    path: string;
    url: string
}

export class SocketService {

    private static _instances: SocketItem[] = new Array<SocketItem>();

    private static getInstance(name: string, pathName?: string): SocketItem | undefined {
        return SocketService._instances.find(x => x.name === name && x.pathName === pathName);
    }

    //This should really come from the registered modules and their settings.
    private static getConnectionInfo(module: string, pathName?: string): ConnectionInfo {
        let url: string;
        let path: string;
        let uri: URL;


        let settings = getAppSettings();
        let production = settings.production;
        switch (module.toLowerCase()) {
            case 'fbx':
                url = settings.fabrixSockets as string;

                path = `/socket/fbx/${pathName}` //default full path here, if specified the port we remove the /socket/ath which is for the reverse proxy.
                if (url.startsWith("http")) {
                    uri = new URL(url)
                    if (uri.port && !uri.port.endsWith("00"))
                        path = `/${pathName}`
                }

                return {
                    url: url,
                    path: path
                }

            case 'tcm':
                url = settings.tcmSockets as string;
                path = `/socket/tcm/${pathName}` //default full path here, if specified the port we remove the /socket/ath which is for the reverse proxy.
                path = ApiService.cleanUrl(path);
                if (url.startsWith("http")) {
                    uri = new URL(url)
                    if (uri.port && !uri.port.endsWith("00"))
                        path = `/${pathName}`
                }
                return {
                    url: url,
                    path: path
                }
            case 'das':
                url = settings.docChatSockets as string;
                path = `/socket/das/${pathName}` //default full path here, if specified the port we remove the /socket/ath which is for the reverse proxy.
                path = ApiService.cleanUrl(path);
                if (url.startsWith("http")) {
                    uri = new URL(url)
                    if (uri.port && !uri.port.endsWith("00"))
                        path = `/${pathName}`
                }
                return {
                    url: url,
                    path: path
                }

            case 'gds':
                url = settings.graphDataSockets as string;
                path = `/socket/gds/${pathName}` //default full path here, if specified the port we remove the /socket/ath which is for the reverse proxy.
                path = ApiService.cleanUrl(path);
                if (url.startsWith("http")) {
                    uri = new URL(url)
                    if (uri.port && !uri.port.endsWith("00"))
                        path = `/${pathName}`
                }
                return {
                    url: url,
                    path: path
                }
            default:
                return {
                    url: settings.fabrixSockets as string,
                    path: production ? `/socket/fbx/${pathName}` : `/${pathName}`
                }
        }


        return {
            url: "",
            path: ""
        }
    }

    private static async getToken() {
        let token = "";


        let isValid = JwtAuthService.isAuthenticated();
        if (!isValid) {
            return "";
        }

        if (shouldRefreshToken()) {
            let service = new JwtAuthService();
            token = await service.silentLogin();
            setTokenRefresh();
        } else {
            const currentToken = UserCache.idToken;
            token = currentToken;
        }

        return token;
    }

    public static async getSocket(name: string, pathName?: string): Promise<Socket | null> {
        let instance = SocketService.getInstance(name, pathName);
        if (!instance || instance.socket === null) {
            let connectionInfo = SocketService.getConnectionInfo(name, pathName);
            const token = await SocketService.getToken();
            if (!token) {
                console.error(`Failed to get token for socket connection for ${name} at ${connectionInfo.url} with path ${connectionInfo.path}`);
                return null;
            }

            let options = {
                query: {},
                path: connectionInfo.path,
                auth: { token },
                transports: ['polling', 'websocket'],
                upgrade: true
            };

            let socket = io(connectionInfo.url, options);

            socket.on('connect_error', (error: Error) => {
                console.error(`Failed to connect to socket for ${name} at ${connectionInfo.url} with path ${connectionInfo.path}`, error);
            });

            if (instance) {
                instance.socket = socket;
            } else {
                instance = {
                    name: name,
                    pathName: pathName,
                    socket: socket
                };
                SocketService._instances.push(instance);
            }

        }
        return instance.socket
    }

    public static reset(name: string, pathName?: string) {
        let instances = SocketService._instances.filter(x => x.name === name && (pathName === undefined || x.pathName === pathName));
        for (let instance of instances) {
            let socket = instance.socket;
            if (socket) {
                socket.disconnect();
                instance.socket = null
            }
        }
    }

    public static async reconnect(token?: string) {
        const nextToken = token ?? await SocketService.getToken();

        if (!nextToken) {
            return;
        }


        const instances = SocketService._instances

        for (const instance of instances) {
            const socket = instance.socket;

            if (socket) {
                socket.auth = { token: nextToken };
                socket.disconnect();
                socket.connect();
            }
        }
    }
}


